import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, gbp, downloadPdf, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Download, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import POPayments from "@/components/POPayments";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_CUSTOMER = { name: "", address_line1: "", address_line2: "", city: "", postcode: "", country: "United Kingdom", is_vat_registered: false, vat_number: "" };
const EMPTY_LINE = { description: "", quantity: 1, rate: 0, amount: 0 };

export default function PurchaseOrderForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [terms, setTerms] = useState(
    "Payment will be made within 30 days of receipt of a valid invoice. " +
    "All services must be carried out as agreed and in accordance with this Purchase Order. " +
    "Invoices must match the agreed rates, dates, and quantities. " +
    "D4PS Couriers & Logistics Ltd reserves the right to dispute or withhold payment for any discrepancies or unauthorised charges."
  );
  const [notes, setNotes] = useState("");
  const [number, setNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [payInfo, setPayInfo] = useState(null);

  useEffect(() => {
    api.get("/contacts", { params: { type: "customer" } }).then((r) => setCustomers(r.data));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/purchase-orders/${id}`).then((r) => {
      const po = r.data;
      setCustomerId(po.customer_id || "");
      setCustomer({ ...EMPTY_CUSTOMER, ...(po.customer_snapshot || {}) });
      setDate((po.date || "").slice(0, 10));
      setReference(po.reference || "");
      setTaxRate(po.tax_rate);
      setLineItems(po.line_items.length ? po.line_items : [{ ...EMPTY_LINE }]);
      setTerms(po.terms || "");
      setNotes(po.notes || "");
      setNumber(po.number);
      setPayInfo({ total: po.total, payments: po.payments || [] });
    });
  }, [id, isEdit]);

  const onCustomerSelect = (val) => {
    if (val === "__manual") {
      setCustomerId("");
      return;
    }
    setCustomerId(val);
    const c = customers.find((x) => x.id === val);
    if (c) {
      setCustomer({
        name: c.name,
        address_line1: c.address_line1,
        address_line2: c.address_line2,
        city: c.city,
        postcode: c.postcode,
        country: c.country || "United Kingdom",
        is_vat_registered: !!c.is_vat_registered,
        vat_number: c.vat_number || "",
      });
      // Apply VAT only when the subcontractor is VAT-registered
      setTaxRate(c.is_vat_registered ? 20 : 0);
    }
  };

  const updateLine = (i, patch) => {
    setLineItems((prev) => {
      const next = prev.map((li, idx) => (idx === i ? { ...li, ...patch } : li));
      const li = next[i];
      const qty = Number(li.quantity) || 0;
      const rate = Number(li.rate) || 0;
      // Auto-calc amount if neither explicitly typed in 'amount'; we'll always compute unless user types amount manually.
      if (patch.quantity !== undefined || patch.rate !== undefined) {
        next[i].amount = +(qty * rate).toFixed(2);
      }
      return next;
    });
  };

  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);
    const tax = +(subtotal * (Number(taxRate) || 0) / 100).toFixed(2);
    return { subtotal: +subtotal.toFixed(2), tax, total: +(subtotal + tax).toFixed(2) };
  }, [lineItems, taxRate]);

  const submit = async () => {
    if (!customer.name) return toast.error("Please enter or select a subcontractor.");
    if (!lineItems.length || lineItems.every((li) => !li.description)) {
      return toast.error("Please add at least one line item.");
    }
    const payload = {
      customer_id: customerId || null,
      customer_snapshot: customer,
      date,
      reference,
      tax_rate: Number(taxRate),
      line_items: lineItems
        .filter((li) => li.description || Number(li.amount))
        .map((li) => ({
          description: li.description,
          quantity: Number(li.quantity) || 0,
          rate: Number(li.rate) || 0,
          amount: Number(li.amount) || 0,
        })),
      terms,
      notes,
    };
    setSaving(true);
    try {
      const res = isEdit
        ? await api.put(`/purchase-orders/${id}`, payload)
        : await api.post("/purchase-orders", payload);
      toast.success(`${res.data.number} saved`);
      if (isEdit) setPayInfo({ total: res.data.total, payments: res.data.payments || [] });
      navigate(`/purchase-orders/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async () => {
    if (!isEdit) return toast.info("Save the PO first to download a PDF.");
    try {
      await downloadPdf(`/purchase-orders/${id}/pdf`, `${number || "PO"}.pdf`);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  return (
    <div data-testid="po-form-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/purchase-orders" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Back to Purchase Orders
          </Link>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">
            {isEdit ? `Purchase Order ${number}` : "New Purchase Order"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
            <Button data-testid="po-download-btn" variant="outline" className="rounded-sm border-slate-300" onClick={onDownload}>
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          )}
          <Button data-testid="po-save-btn" onClick={submit} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Save className="h-4 w-4 mr-2" /> {isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>

      {/* Customer + meta */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-sm border border-slate-200 shadow-none p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Bill To</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Subcontractor (saved)</Label>
              <Select value={customerId || "__manual"} onValueChange={onCustomerSelect}>
                <SelectTrigger data-testid="po-customer-select" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue placeholder="Choose a subcontractor or type manually" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__manual">— Type manually —</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field label="Company name" testid="po-cust-name" value={customer.name}
              onChange={(v) => setCustomer({ ...customer, name: v })} />
            <Field label="Address line 1" testid="po-cust-addr1" value={customer.address_line1}
              onChange={(v) => setCustomer({ ...customer, address_line1: v })} />
            <Field label="Address line 2" testid="po-cust-addr2" value={customer.address_line2}
              onChange={(v) => setCustomer({ ...customer, address_line2: v })} />
            <Field label="City" testid="po-cust-city" value={customer.city}
              onChange={(v) => setCustomer({ ...customer, city: v })} />
            <Field label="Postcode" testid="po-cust-postcode" value={customer.postcode}
              onChange={(v) => setCustomer({ ...customer, postcode: v })} />
            <Field label="Country" testid="po-cust-country" value={customer.country}
              onChange={(v) => setCustomer({ ...customer, country: v })} />
          </div>
        </Card>
        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">PO Details</div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">Date</Label>
              <Input data-testid="po-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Reference</Label>
              <Input
                data-testid="po-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Job ref, supplier quote ref, etc."
                className="mt-1 rounded-sm border-slate-300"
              />
              <p className="text-[11px] text-slate-500 mt-1">Optional — shown on the PDF beneath the date.</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Tax rate (%)</Label>
              <Input data-testid="po-tax" type="number" min="0" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
              <p className="text-[11px] text-slate-500 mt-1">
                Auto-set from the subcontractor's VAT status. Override here if needed.
              </p>
            </div>
            {isEdit && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500">Reference</div>
                <div className="font-display text-xl font-semibold text-slate-900">{number}</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Line items */}
      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Line items</div>
          <Button data-testid="po-add-line" size="sm" variant="outline" className="rounded-sm border-slate-300"
            onClick={() => setLineItems([...lineItems, { ...EMPTY_LINE }])}>
            <Plus className="h-4 w-4 mr-1" /> Add line
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full po-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-semibold">Item</th>
                <th className="px-5 py-3 font-semibold text-right w-24">Quantity</th>
                <th className="px-5 py-3 font-semibold text-right w-32">Rate (£)</th>
                <th className="px-5 py-3 font-semibold text-right w-32">Amount (£)</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-5 py-2">
                    <Input data-testid={`po-line-desc-${i}`} value={li.description}
                      onChange={(e) => updateLine(i, { description: e.target.value })}
                      placeholder="e.g. x2 drivers provided at Sheffield depot"
                      className="rounded-sm border-slate-300" />
                  </td>
                  <td className="px-5 py-2">
                    <Input data-testid={`po-line-qty-${i}`} type="number" min="0" step="0.01" value={li.quantity}
                      onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      className="rounded-sm border-slate-300 text-right" />
                  </td>
                  <td className="px-5 py-2">
                    <Input data-testid={`po-line-rate-${i}`} type="number" min="0" step="0.01" value={li.rate}
                      onChange={(e) => updateLine(i, { rate: e.target.value })}
                      className="rounded-sm border-slate-300 text-right" />
                  </td>
                  <td className="px-5 py-2">
                    <Input data-testid={`po-line-amount-${i}`} type="number" min="0" step="0.01" value={li.amount}
                      onChange={(e) => updateLine(i, { amount: Number(e.target.value) })}
                      className="rounded-sm border-slate-300 text-right" />
                  </td>
                  <td className="px-5 py-2 text-right">
                    <Button data-testid={`po-line-remove-${i}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-slate-500 hover:text-red-600"
                      onClick={() => setLineItems(lineItems.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
          <div className="w-full max-w-xs space-y-2 text-sm" data-testid="po-totals">
            <Row label="Subtotal" value={gbp(totals.subtotal)} />
            {Number(taxRate) > 0 && (
              <Row label={`VAT (${Number(taxRate)}%)`} value={gbp(totals.tax)} muted />
            )}
            <div className="border-t border-slate-200 pt-2 flex justify-between font-display font-semibold text-base text-slate-900">
              <span>Total</span>
              <span className="tabular-nums">{gbp(totals.total)}</span>
            </div>
            {Number(taxRate) === 0 && (
              <div className="text-[11px] text-slate-400 pt-1">No VAT applied — subcontractor is not VAT registered.</div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Terms</Label>
          <Textarea data-testid="po-terms" rows={6} value={terms} onChange={(e) => setTerms(e.target.value)}
            className="mt-3 rounded-sm border-slate-300" />
        </Card>
        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Notes (internal)</Label>
          <Textarea data-testid="po-notes" rows={6} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="mt-3 rounded-sm border-slate-300" placeholder="Optional internal notes…" />
        </Card>
      </div>
      {isEdit && payInfo && (
        <Card className="rounded-sm border border-slate-200 shadow-none p-6" data-testid="po-form-payments">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Payment history</Label>
          <div className="mt-3">
            <POPayments poId={id} total={payInfo.total} payments={payInfo.payments} onChange={(d) => setPayInfo((p) => ({ ...p, payments: d.payments }))} />
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm border-slate-300" />
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-slate-500" : "text-slate-800"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
